import { apiClient } from "@/api/client";
import type {
  AdminDashboardResponse,
  AdminPendingResolutionsResponse,
  AdminMarketsResponse,
  AdminUsersResponse,
  AdminSystemHealthResponse,
  AdminBanUserRequest,
  AdminActionableMarketsResponse,
  MarketStatus,
} from "../types/admin";
import type { Role } from "@/features/admin/types/admin";

/**
 * Admin API client.
 *
 * 2026-06-12 admin-auth alignment: there is no separate admin token anymore.
 * Every call rides the shared `apiClient`, which attaches the connected
 * wallet's SIWS JWT; the backend gates each route by KV role (operator/
 * admin). The old bearer==ADMIN_WALLET_SECRET path and `verifyAdminToken`
 * probe are gone.
 */

const ADMIN_BASE = "/api/admin";

function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// --- Role probe (users route, anonymous-friendly) ---

interface MyRoleResponse {
  success: boolean;
  data: { wallet: string | null; role: Role };
}

/** The connected wallet's KV role — drives the admin guard / nav. */
export async function fetchMyRole(): Promise<Role> {
  const res = await apiClient.get<MyRoleResponse>("/api/users/me/role");
  return res.data.role;
}

// --- Dashboard ---

export async function fetchDashboard(): Promise<AdminDashboardResponse> {
  return apiClient.get<AdminDashboardResponse>(`${ADMIN_BASE}/dashboard`);
}

// --- Pending Resolutions ---

export async function fetchPendingResolutions(): Promise<AdminPendingResolutionsResponse> {
  return apiClient.get<AdminPendingResolutionsResponse>(
    `${ADMIN_BASE}/pending-resolutions`,
  );
}

// --- Market Management ---

export async function fetchAdminMarkets(params: {
  status?: MarketStatus;
  market_mode?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  order?: "asc" | "desc";
}): Promise<AdminMarketsResponse> {
  return apiClient.get<AdminMarketsResponse>(
    `${ADMIN_BASE}/markets${qs(params)}`,
  );
}

/** Resolved markets + chain readiness — the admin's close/drain worklist. */
export async function fetchActionableMarkets(): Promise<AdminActionableMarketsResponse> {
  return apiClient.get<AdminActionableMarketsResponse>(
    `${ADMIN_BASE}/markets/actionable`,
  );
}

// --- User Management (admin-only on the backend) ---

export async function fetchAdminUsers(params: {
  search?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  order?: "asc" | "desc";
}): Promise<AdminUsersResponse> {
  return apiClient.get<AdminUsersResponse>(`${ADMIN_BASE}/users${qs(params)}`);
}

export async function banUser(
  data: AdminBanUserRequest,
): Promise<{ success: boolean }> {
  return apiClient.post<{ success: boolean }>(`${ADMIN_BASE}/users/ban`, data);
}

// --- System Health ---

export async function fetchSystemHealth(): Promise<AdminSystemHealthResponse> {
  return apiClient.get<AdminSystemHealthResponse>(
    `${ADMIN_BASE}/system-health`,
  );
}
